import React, { useMemo, useState } from 'react';
import { Minus, Plus } from 'lucide-react';

const rollTypes = {
  white: { name: 'Helles Brötchen', price: 0.30 },
  seeded: { name: 'Körnerbrötchen', price: 0.40 },
};

const toppings = [
  { id: 'cheese', name: 'Käse', price: 0.50 },
  { id: 'sausage', name: 'Wurst', price: 0.50 },
  { id: 'jam', name: 'Marmelade', price: 0.20 },
];

const currency = value => new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
}).format(value);

const LocalBreakfastPreview = () => {
  const [assignments, setAssignments] = useState([]);
  const [boiledEggs, setBoiledEggs] = useState(0);
  const [hasCoffee, setHasCoffee] = useState(false);

  const changeRoll = (type, delta) => {
    setAssignments(current => {
      if (delta > 0) {
        const number = current.filter(item => item.type === type).length + 1;
        return [...current, {
          id: `${type}-${Date.now()}-${number}`,
          type,
          label: `${rollTypes[type].name} ${number}`,
          topping: '',
        }];
      }
      const removeIndex = current.map(item => item.type).lastIndexOf(type);
      return removeIndex < 0 ? current : current.filter((_, index) => index !== removeIndex);
    });
  };

  const setTopping = (index, topping) => {
    setAssignments(current => current.map((assignment, assignmentIndex) => (
      assignmentIndex === index ? { ...assignment, topping } : assignment
    )));
  };

  const rollCounts = useMemo(() => ({
    white: assignments.filter(item => item.type === 'white').length,
    seeded: assignments.filter(item => item.type === 'seeded').length,
  }), [assignments]);
  const selectedToppings = assignments.filter(item => item.topping).length;
  const total = assignments.reduce((sum, assignment) => {
    const topping = toppings.find(item => item.id === assignment.topping);
    return sum + rollTypes[assignment.type].price + (topping?.price || 0);
  }, 0) + boiledEggs * 0.50 + (hasCoffee ? 0.50 : 0);

  return (
    <div className="space-y-4" data-testid="local-breakfast-preview">
      <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
        <div className="mb-4 flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500 text-xs font-bold text-white">1</span><h3 className="text-sm font-semibold text-gray-800">Brötchen wählen</h3></div>
        <div className="grid gap-3 sm:grid-cols-2">
          {Object.entries(rollTypes).map(([type, roll]) => (
            <div key={type} className="rounded-xl border border-amber-100 bg-white p-3">
              <div className="flex items-center justify-between gap-3"><span className="text-sm font-medium text-gray-700">{roll.name}</span><span className="text-xs text-gray-500">{currency(roll.price)}/Hälfte</span></div>
              <div className="mt-3 flex items-center justify-between">
                <button type="button" onClick={() => changeRoll(type, -1)} disabled={!rollCounts[type]} aria-label={`${roll.name} entfernen`} className="flex h-8 w-8 items-center justify-center rounded-lg border border-amber-200 text-amber-800 disabled:opacity-30"><Minus className="h-3.5 w-3.5" /></button>
                <div className="text-center"><div className="text-lg font-semibold text-gray-900">{rollCounts[type]}</div><div className="text-[11px] text-gray-400">Hälften</div></div>
                <button type="button" onClick={() => changeRoll(type, 1)} aria-label={`${roll.name} hinzufügen`} className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500 text-white active:scale-[0.97]"><Plus className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {assignments.length > 0 && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
          <div className="mb-2 flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-500 text-xs font-bold text-white">2</span><h3 className="text-sm font-semibold text-gray-800">Beläge zuweisen</h3></div>
          <p className="mb-4 text-xs text-gray-500">Wähle wie in der echten Bestellmaske für jede Brötchenhälfte einen Belag.</p>
          <div className="space-y-2">
            {assignments.map((assignment, index) => (
              <div key={assignment.id} className="grid gap-2 rounded-lg border border-emerald-200 bg-white p-3 sm:grid-cols-[180px_1fr] sm:items-center">
                <label htmlFor={`tutorial-topping-${assignment.id}`} className="text-xs font-medium text-gray-700">{assignment.label}</label>
                <select id={`tutorial-topping-${assignment.id}`} aria-label={`Belag für ${assignment.label}`} value={assignment.topping} onChange={event => setTopping(index, event.target.value)} className="w-full rounded-lg border border-emerald-200 bg-white px-3 py-2 text-sm text-gray-700">
                  <option value="">Belag wählen...</option>
                  {toppings.map(topping => <option key={topping.id} value={topping.id}>{topping.name}{topping.price ? ` (+${currency(topping.price)})` : ''}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="mt-3 text-right text-xs font-medium text-emerald-700">{selectedToppings} / {assignments.length} Beläge gewählt</div>
        </div>
      )}

      <div className="rounded-xl border border-yellow-100 bg-yellow-50 p-4">
        <div className="mb-3 text-sm font-semibold text-gray-800">Extras hinzufügen</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-center justify-between rounded-lg border border-yellow-100 bg-white p-3"><div><div className="text-sm font-medium text-gray-700">Frühstücksei</div><div className="text-xs text-gray-500">0,50 €/Stück</div></div><div className="flex items-center gap-2"><button type="button" onClick={() => setBoiledEggs(value => Math.max(0, value - 1))} disabled={!boiledEggs} className="h-7 w-7 rounded-md border disabled:opacity-30">−</button><span className="w-4 text-center text-sm">{boiledEggs}</span><button type="button" onClick={() => setBoiledEggs(value => value + 1)} className="h-7 w-7 rounded-md bg-yellow-500 text-white">+</button></div></div>
          <label className="flex cursor-pointer items-center justify-between rounded-lg border border-yellow-100 bg-white p-3"><div><div className="text-sm font-medium text-gray-700">Kaffee</div><div className="text-xs text-gray-500">0,50 €</div></div><input type="checkbox" checked={hasCoffee} onChange={event => setHasCoffee(event.target.checked)} className="h-4 w-4" /></label>
        </div>
      </div>

      <div className="rounded-xl bg-gray-900 p-4 text-white">
        <div className="flex items-center justify-between"><div><div className="text-sm font-semibold">Bestellübersicht</div><div className="mt-1 text-xs text-gray-400">Keine echte Bestellung oder Buchung</div></div><div className="text-xl font-semibold">{currency(total)}</div></div>
      </div>
    </div>
  );
};

export default LocalBreakfastPreview;
